import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { SetStateAction } from 'preact/compat';
import { Dispatch, useMemo, useState } from 'preact/hooks';

import ProductIntervalSelector from '@/components/products/ProductIntervalSelector';
import ProductPrice from '@/components/products/ProductPrice';
import { ProductType } from '@/components/products/ProductsPromptModal/redux/types';
import { parseBenefits } from '@/components/products/utils';
import { getProductsQueryOptions } from '@/queries/products';
import { ParsedUserProduct, Product, ProductInterval } from '@/queries/products/types';

import PromotedProductSection from './PromotedProductSection';
import UserProductImage from './UserProductImage';
import { EDUCATOR_PAIR_LOOKUP_KEYS, EXPLORER_PAIR_LOOKUP_KEYS, selectProductsMap } from './utils';

interface RecurringPromotedProductSectionProps {
  productType: ProductType;
  userProduct: ParsedUserProduct;
  onSwitch: (product: Product) => void;
  isPending: boolean;
  onResume: () => void;
  processingButtonId: string | null;
  setProcessingButtonId: Dispatch<SetStateAction<string | null>>;
}

/** Tier-change promotion for active Stripe explorer/educator subscriptions */
const RecurringPromotedProductSection = ({
  productType,
  userProduct,
  onSwitch,
  isPending,
  onResume,
  processingButtonId,
  setProcessingButtonId,
}: RecurringPromotedProductSectionProps) => {
  const isPromotingEducatorPass = productType === 'educator-pass';
  const [interval, setInterval] = useState<ProductInterval>('month');

  const { data: productsMap, isLoading } = useQuery({
    ...getProductsQueryOptions({
      lookupKey: isPromotingEducatorPass ? EDUCATOR_PAIR_LOOKUP_KEYS : EXPLORER_PAIR_LOOKUP_KEYS,
    }),
    select: selectProductsMap,
  });

  const monthlyProduct = productsMap?.get('monthly-member') || productsMap?.get('monthly-educator');
  const yearlyProduct = productsMap?.get('annual-member') || productsMap?.get('annual-educator');

  const product = interval === 'month' ? monthlyProduct : yearlyProduct;
  const isSwitchingToProduct = userProduct.scheduledProductId === product?._id;

  const benefits = useMemo(() => parseBenefits(product), [product]);

  if (isLoading || !product) {
    return null;
  }

  return (
    <PromotedProductSection
      image={<UserProductImage className="size-11 shrink-0 desktop:size-[60px]" />}
      title={isPromotingEducatorPass ? 'Educator Plan' : 'Explorer Plan'}
      subtitle={
        isPromotingEducatorPass ? 'Unlimited learning on top of existing access.' : 'Unlimited discovery and delight.'
      }
      priceRow={
        <>
          <ProductPrice currency={product.price.currency} price={product.price.price} />
          <ProductIntervalSelector value={interval} onChange={setInterval} />
        </>
      }
      benefitLines={benefits}
      primaryButton={{
        label: isSwitchingToProduct ? 'Cancel Switch' : isPromotingEducatorPass ? 'Upgrade Plan' : 'Switch Plan',
        variant: isSwitchingToProduct ? 'outline' : 'default',
        disabled: isPending,
        loading: isPending && processingButtonId === 'promoted-product-button',
        dataId: 'promoted-product-button',
        onClick: () => {
          setProcessingButtonId('promoted-product-button');
          if (isSwitchingToProduct) {
            onResume();
          } else {
            onSwitch(product);
          }
        },
      }}
      helperContent={
        isPromotingEducatorPass ? (
          "Your upgraded access starts right away, and you'll only be charged the prorated difference for the rest of your current billing cycle."
        ) : (
          <>By switching, you will pay on {format(userProduct.validUntil, 'MMMM d yyyy')}, after your current plan expires</>
        )
      }
    />
  );
};

export default RecurringPromotedProductSection;
